import { useMemo, useState } from 'react';
import { Panel, Toolbar } from './Page';
import { ErrorState, InlineError, TableSkeleton } from './Feedback';
import { Chip } from './Chips';
import { CheckboxField, TextField } from './Form';
import { TableWrap } from './Table';
import { documents, emailIntake } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { ApiError, errorMessage } from '../lib/api';
import { DocumentStatus, DocumentStatusLabels } from '../lib/enums';
import type {
  DocumentDetailDto,
  DocumentOverrideRequest,
  VendorIntakeFieldComparison,
} from '../lib/types';
import { VendorUpdateStates } from '../lib/types';

/*
 * WHY THIS LIVES IN components/ AND NOT IN THE PAGE.
 *
 * The same review is now reached from two places - the Vendor Intake page, and the Invoice template
 * section of a vendor card - and the decisions it encodes (nothing is created until a person
 * approves it; an existing supplier is compared field by field and never pre-ticked; bank details
 * are shown but never applied or learned) must be identical in both. Copying it would create a
 * second place where a supplier can be written and iERP AI taught, which is exactly the kind of
 * second authority this codebase refuses elsewhere. One component, two callers.
 */

/**
 * The review itself: what was read, what the operator makes of it, and the two decisions available -
 * use a supplier QuickBooks already has, or create one.
 *
 * THOSE TWO DECISIONS ARE NOT THE SAME SHAPE, and this screen stopped pretending they were.
 *
 * Creating a supplier sets every value for the first time, so a plain form is honest: there is
 * nothing to overwrite. Updating one that already exists is a proposal to REPLACE values somebody
 * already maintains, from an invoice PDF - which is not automatically the better source. Presenting
 * that as a pre-filled form invites approving an overwrite nobody compared: the operator sees a
 * plausible address, says yes, and a good QuickBooks value is replaced by a worse one read off a
 * document. So for an existing supplier the screen shows three columns - what QuickBooks holds now,
 * what this document suggests, and an explicitly unticked choice - and sends ONLY what was ticked.
 * Nothing ticked means no update call at all.
 */
export function VendorIntakeReview({
  detail,
  documentId,
  boundVendorId,
  onDone,
}: {
  detail: DocumentDetailDto;
  documentId: string;
  boundVendorId: string | null;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const vendor = detail.contract?.vendor ?? null;
  const overrides = detail.overrides ?? null;
  const match = detail.suggestedVendorMatch ?? null;
  const posted = detail.summary.status === DocumentStatus.Posted;

  // WHICH SUPPLIER THIS INTAKE IS ABOUT. Bound on arrival from a vendor card, or chosen here from a
  // match. Null means "create a new one", which is the only case where a plain form is right.
  const [chosenVendorId, setChosenVendorId] = useState<string | null>(boundVendorId);

  const comparison = useAsync(
    (signal) =>
      chosenVendorId
        ? documents.vendorIntakeComparison(documentId, chosenVendorId, signal)
        : Promise.resolve(null),
    [documentId, chosenVendorId],
  );

  // WHAT THE OPERATOR TICKED. Empty is the default and the safe answer: keep everything QuickBooks
  // already has. It is never seeded from the comparison, because a pre-ticked box is a decision the
  // screen made on the operator's behalf, which is the whole failure being fixed.
  const [apply, setApply] = useState<Record<string, boolean>>({});

  // WHAT THE OPERATOR TYPED. Two wrong values is a real case: QuickBooks says "ABC Supply", the
  // invoice says "ABC Supplies Limited", and the person doing the work knows it is "ABC Supplies
  // Ltd." Offering only those two forces them to accept something they know is wrong, or to leave
  // the record wrong and fix it elsewhere where nothing is learned from the correction.
  //
  // Editing is NOT applying. A row still does nothing until its box is ticked - typing a better
  // value is preparing a choice, not making one, and conflating them would let a stray keystroke
  // overwrite a supplier.
  const [edits, setEdits] = useState<Record<string, string>>({});

  /** The value a row would write: what the operator typed, or the document's suggestion. */
  const proposedValue = (row: VendorIntakeFieldComparison): string =>
    edits[row.field] ?? row.suggestedValue ?? '';

  const initial = useMemo(
    () => ({
      vendorName: overrides?.vendorName ?? vendor?.name ?? '',
      vendorEmail: overrides?.vendorEmail ?? vendor?.email ?? '',
      vendorPhone: overrides?.vendorPhone ?? vendor?.phone ?? '',
      vendorTaxRegistrationNo:
        overrides?.vendorTaxRegistrationNo ?? vendor?.taxRegistrationNo ?? '',
      vendorAddressLine1: overrides?.vendorAddressLine1 ?? vendor?.address ?? '',
      vendorAddressLine2: overrides?.vendorAddressLine2 ?? vendor?.address2 ?? '',
      vendorCity: overrides?.vendorCity ?? vendor?.city ?? '',
      vendorState: overrides?.vendorState ?? vendor?.county ?? '',
      vendorPostalCode: overrides?.vendorPostalCode ?? vendor?.postCode ?? '',
      vendorCountry: overrides?.vendorCountry ?? vendor?.countryRegionCode ?? '',
      currencyCode: overrides?.currencyCode ?? detail.contract?.header?.currencyCode ?? '',
    }),
    [overrides, vendor, detail.contract],
  );

  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set when a refusal was specifically the bill-to-company gate (CompanyMismatch), so the admin
  // override below only ever appears for that one reason — never as a generic "try anyway" escape
  // hatch for other refusals. Real vendors sometimes have the wrong client name or address on file,
  // which is a normal cost of doing business, not a reason to leave the invoice stuck forever.
  const [companyMismatch, setCompanyMismatch] = useState(false);
  const [overriding, setOverriding] = useState<'Accept' | 'Reject' | null>(null);

  // The bill for this same invoice, offered once the supplier exists.
  const [billing, setBilling] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const rows = comparison.data?.fields ?? [];
  const tickedCount = rows.filter(
    (row) => apply[row.field] && row.editable && proposedValue(row).trim().length > 0,
  ).length;

  const emptyOverrides = (): DocumentOverrideRequest => ({
    vendorName: null,
    qboVendorId: null,
    invoiceNumber: null,
    invoiceDate: null,
    dueDate: null,
    total: null,
    currencyCode: null,
    memo: null,
    lines: null,
    vendorEmail: null,
    vendorPhone: null,
    vendorTaxRegistrationNo: null,
    vendorAddressLine1: null,
    vendorAddressLine2: null,
    vendorCity: null,
    vendorState: null,
    vendorPostalCode: null,
    vendorCountry: null,
  });

  /** Every value, because creating a supplier overwrites nothing. */
  const creationOverrides = (): DocumentOverrideRequest => ({
    ...emptyOverrides(),
    vendorName: form.vendorName || null,
    currencyCode: form.currencyCode || null,
    vendorEmail: form.vendorEmail || null,
    vendorPhone: form.vendorPhone || null,
    vendorTaxRegistrationNo: form.vendorTaxRegistrationNo || null,
    vendorAddressLine1: form.vendorAddressLine1 || null,
    vendorAddressLine2: form.vendorAddressLine2 || null,
    vendorCity: form.vendorCity || null,
    vendorState: form.vendorState || null,
    vendorPostalCode: form.vendorPostalCode || null,
    vendorCountry: form.vendorCountry || null,
  });

  /**
   * ONLY WHAT WAS TICKED. An unticked field is absent from the payload, and an absent field is not
   * written - so "keep what QuickBooks has" is expressed by sending nothing at all rather than by
   * sending the current value back, which would be a write that could still fail or race.
   */
  const selectedOverrides = (): DocumentOverrideRequest => {
    const body = emptyOverrides() as unknown as Record<string, unknown>;

    for (const row of rows) {
      if (!row.editable || !apply[row.field]) continue;

      // The operator's own value wins over the AI's. If they corrected the proposal, that
      // correction is what QuickBooks is written with AND what iERP AI is taught - the two can
      // never be allowed to differ, because the whole point is that the learned baseline matches
      // the master record.
      const value = proposedValue(row).trim();
      if (!value) continue;

      body[row.field] = value;
    }

    return body as unknown as DocumentOverrideRequest;
  };

  const approve = async (useExistingQboVendorId: string | null) => {
    setBusy(true);
    setError(null);
    setCompanyMismatch(false);

    const body = useExistingQboVendorId ? selectedOverrides() : creationOverrides();

    try {
      const result = await documents.approveVendorIntake(documentId, {
        overrides: body,
        useExistingQboVendorId,
      });

      if (!result.posting?.success) {
        // THE REAL WRITE RESULT, NOT A WARNING. A refused or unanswered vendor update ends the
        // approval: the supplier is unchanged, the intake stays in review, and nothing was learned.
        setError(result.posting?.vendorUpdate?.message ?? result.posting?.message
          ?? 'The vendor could not be saved.');
        return;
      }

      if (result.posting.vendorUpdate?.state === VendorUpdateStates.NotAttempted) {
        toast.success('Nothing was changed in QuickBooks - you kept every current value.');
      } else if (result.posting.vendorUpdate?.state === VendorUpdateStates.Applied) {
        toast.success(result.posting.vendorUpdate.message);
      }

      // Learning is reported separately from the vendor: the supplier exists either way, and a
      // confirmation that did not land is worth saying out loud rather than hiding.
      if (result.learning?.attempted && !result.learning.success) {
        toast.warn(result.learning.message ?? 'The vendor was saved, but nothing was learned.');
      } else if (result.learning?.success) {
        toast.success('iERP AI has learned what this supplier\u2019s invoices look like.');
      }

      // The invoice this intake was handed off from, if there was one.
      if (result.resume?.changed) toast.success(result.resume.message);

      await onDone();
    } catch (caught) {
      setError(errorMessage(caught));
      if (caught instanceof ApiError && caught.code === 'CompanyMismatch') setCompanyMismatch(true);
    } finally {
      setBusy(false);
    }
  };

  /**
   * ADMIN-ONLY, AND DELIBERATELY A SEPARATE STEP FROM APPROVING.
   *
   * Confirming the invoice belongs here does not post it — it only lifts the bill-to-company gate
   * (the server's held-invoice override, shared with the Email Intake queue) so Approve can be
   * clicked again. A vendor who has the wrong client name or address on file is common enough that
   * refusing forever would be worse than trusting a person who looked at the document and said yes.
   */
  const overrideCompanyMatch = (decision: 'Accept' | 'Reject') => {
    setOverriding(decision);
    void (async () => {
      try {
        await emailIntake.resolveHeldInvoice(documentId, { decision });
        if (decision === 'Accept') {
          setCompanyMismatch(false);
          setError(null);
          toast.success('Confirmed', 'This invoice is now treated as belonging to this company. Approve it again to continue.');
        } else {
          toast.success('Rejected', 'This invoice was closed as not belonging to this company.');
          await onDone();
        }
      } catch (caught) {
        toast.error('Could not resolve the company mismatch', errorMessage(caught));
      } finally {
        setOverriding(null);
      }
    })();
  };

  const reject = async () => {
    // A DELIBERATE DECISION, NOT AN ABANDONMENT. Navigating away leaves the intake sitting in
    // review; rejecting says the proposal was wrong. Neither writes a vendor, and neither teaches
    // iERP AI anything - learning is only ever reached from the approval route.
    setBusy(true);
    setError(null);

    try {
      await documents.reject(documentId, { reason: 'Vendor intake rejected by the operator.' });
      toast.success('Vendor intake rejected. No vendor was created and nothing was learned.');
      await onDone();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  /**
   * THE INVOICE THAT CREATED THE SUPPLIER STILL HAS TO BE PAID.
   *
   * It is almost always the first invoice from that supplier, and it is already here - stored,
   * hashed and audited. Asking the operator to upload it again is asking them to do the one thing
   * duplicate blocking exists to refuse, so the same evidence is re-read as a bill instead.
   */
  const createBill = async () => {
    setBilling(true);
    setBillError(null);

    try {
      const result = await documents.createBillFromIntake(documentId);
      toast.success(result.message || 'Reading this invoice as a bill.');
      await onDone();
    } catch (caught) {
      setBillError(errorMessage(caught));
    } finally {
      setBilling(false);
    }
  };

  if (posted) {
    return (
      <div className="stack">
        <Chip tone="good">Vendor created</Chip>
        <p className="muted">
          {detail.summary.vendorName} is now in QuickBooks, and iERP AI has this invoice as an
          example of what their invoices look like.
        </p>

        <Panel title="The invoice that set this supplier up">
          <p className="muted">
            This same invoice can now be read as a bill for {detail.summary.vendorName}. It uses the
            file already stored against this document - do not upload it again, which would be
            refused as a duplicate and would give one invoice two records.
          </p>

          {billError ? <InlineError message={billError} /> : null}

          <Toolbar>
            <button
              type="button"
              className="btn btn-primary"
              disabled={billing}
              onClick={() => void createBill()}
            >
              {billing ? 'Reading it as a bill\u2026' : 'Create the bill from this invoice'}
            </button>
          </Toolbar>
        </Panel>
      </div>
    );
  }

  return (
    <div className="stack">
      {detail.summary.status !== DocumentStatus.ReadyForReview &&
      detail.summary.status !== DocumentStatus.PendingApproval ? (
        <Chip tone="neutral">
          {DocumentStatusLabels[detail.summary.status]} - the supplier details appear once the
          analysis finishes.
        </Chip>
      ) : null}

      {detail.summary.vendorIntakeResumeScenario != null ? (
        <Chip tone="info">
          This is an invoice waiting on its supplier. Approving the supplier here puts the same
          document straight back to being that invoice - nothing is uploaded again.
        </Chip>
      ) : null}

      {match && !chosenVendorId ? (
        <Panel title="Possible existing supplier">
          <p>
            <strong>{match.displayName}</strong>{' '}
            <span className="muted">
              matched on {match.matchMethod} ({Math.round(match.confidence * 100)}%)
            </span>
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => setChosenVendorId(match.qboVendorId)}
          >
            Use this supplier
          </button>
        </Panel>
      ) : null}

      {chosenVendorId ? (
        <ExistingVendorComparison
          busy={busy || comparison.loading}
          loading={comparison.loading}
          error={comparison.error ? errorMessage(comparison.error) : null}
          onRetry={comparison.reload}
          vendorName={comparison.data?.qboVendorName ?? null}
          available={comparison.data?.currentValuesAvailable ?? false}
          message={comparison.data?.message ?? null}
          rows={rows}
          apply={apply}
          proposed={proposedValue}
          onToggle={(field, value) => setApply((current) => ({ ...current, [field]: value }))}
          onEdit={(field, value) => setEdits((current) => ({ ...current, [field]: value }))}
        />
      ) : (
        <Panel title="Supplier details">
          <p className="muted">
            Correct anything the analysis got wrong. What you approve is what is saved to QuickBooks
            and what iERP AI learns.
          </p>

          <TextField label="Name" value={form.vendorName} onChange={set('vendorName')} />
          <TextField label="Tax / VAT registration" value={form.vendorTaxRegistrationNo} onChange={set('vendorTaxRegistrationNo')} />
          <TextField label="Email" value={form.vendorEmail} onChange={set('vendorEmail')} />
          <TextField label="Phone" value={form.vendorPhone} onChange={set('vendorPhone')} />
          <TextField label="Address" value={form.vendorAddressLine1} onChange={set('vendorAddressLine1')} />
          <TextField label="Address 2" value={form.vendorAddressLine2} onChange={set('vendorAddressLine2')} />
          <TextField label="City" value={form.vendorCity} onChange={set('vendorCity')} />
          <TextField label="State / county" value={form.vendorState} onChange={set('vendorState')} />
          <TextField label="Postal code" value={form.vendorPostalCode} onChange={set('vendorPostalCode')} />
          <TextField label="Country" value={form.vendorCountry} onChange={set('vendorCountry')} />
          <TextField
            label="Currency"
            value={form.currencyCode}
            onChange={set('currencyCode')}
            hint="Set once, when the supplier is created. QuickBooks does not allow it to change afterwards."
          />
        </Panel>
      )}

      {vendor?.bankName || vendor?.bankAccountNo || vendor?.bankIban ? (
        <Panel title="Bank details on the document">
          {/*
            Shown, never applied and never learned. A supplier's bank account is the field an
            invoice fraud actually targets, and this PDF is an untrusted document. Change bank
            details in QuickBooks, against something other than an emailed invoice.
          */}
          <p className="muted">
            For your information only - these are not saved to QuickBooks and are not learned.
            Verify any bank change directly with the supplier.
          </p>
          <dl className="detail-list">
            {vendor.bankName ? (
              <>
                <dt>Bank</dt>
                <dd>{vendor.bankName}</dd>
              </>
            ) : null}
            {vendor.bankAccountNo ? (
              <>
                <dt>Account</dt>
                <dd>{vendor.bankAccountNo}</dd>
              </>
            ) : null}
            {vendor.bankIban ? (
              <>
                <dt>IBAN</dt>
                <dd>{vendor.bankIban}</dd>
              </>
            ) : null}
          </dl>
        </Panel>
      ) : null}

      {error ? <InlineError message={error} /> : null}

      {companyMismatch ? (
        <Toolbar>
          <button
            type="button"
            className="btn btn-primary"
            disabled={overriding !== null}
            onClick={() => overrideCompanyMatch('Accept')}
          >
            {overriding === 'Accept' ? 'Confirming…' : 'Override — this invoice belongs here'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={overriding !== null}
            onClick={() => overrideCompanyMatch('Reject')}
          >
            {overriding === 'Reject' ? 'Rejecting…' : 'Reject — not ours'}
          </button>
        </Toolbar>
      ) : null}

      <Toolbar>
        {chosenVendorId ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !comparison.data?.currentValuesAvailable}
            onClick={() => void approve(chosenVendorId)}
          >
            {tickedCount === 0
              ? 'Use this supplier unchanged'
              : `Apply ${tickedCount} change${tickedCount === 1 ? '' : 's'} to this supplier`}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !form.vendorName}
            onClick={() => void approve(null)}
          >
            Create supplier
          </button>
        )}

        {chosenVendorId && !boundVendorId ? (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => {
              setChosenVendorId(null);
              setApply({});
            }}
          >
            Create a new supplier instead
          </button>
        ) : null}

        <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void reject()}>
          Reject
        </button>
      </Toolbar>
    </div>
  );
}

/**
 * CURRENT vs SUGGESTED vs APPLY, for a supplier that already exists.
 *
 * Every row starts unticked. That is not a default so much as the answer this screen is built to
 * make easy: keeping what QuickBooks holds must be the effortless choice, and changing it must be a
 * deliberate one, field by field. The current column is read live from QuickBooks - if it could not
 * be read there is no comparison to show and the approval is refused rather than guessed at, because
 * an overwrite decision made against an unknown "before" is not a decision.
 */
function ExistingVendorComparison({
  busy,
  loading,
  error,
  onRetry,
  vendorName,
  available,
  message,
  rows,
  apply,
  proposed,
  onToggle,
  onEdit,
}: {
  busy: boolean;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  vendorName: string | null;
  available: boolean;
  message: string | null;
  rows: VendorIntakeFieldComparison[];
  apply: Record<string, boolean>;
  proposed: (row: VendorIntakeFieldComparison) => string;
  onToggle: (field: string, value: boolean) => void;
  onEdit: (field: string, value: string) => void;
}) {
  if (loading) return <TableSkeleton rows={6} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  if (!available) {
    return (
      <Panel title="This supplier could not be read">
        <InlineError
          message={
            message
            ?? 'That supplier could not be read from QuickBooks, so there is nothing to compare against.'
          }
        />
        <p className="muted">
          Nothing will be changed until its current values can be read. Try again in a moment.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={`Changes to ${vendorName ?? 'this supplier'}`}>
      <p className="muted">{message}</p>

      <TableWrap busy={busy}>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Current in QuickBooks</th>
            <th scope="col">Suggested from this document</th>
            <th scope="col">Apply?</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const value = proposed(row);

            return (
              <tr key={row.field}>
                <th scope="row">{row.label}</th>
                <td>{row.currentValue ?? <span className="muted">(empty)</span>}</td>
                <td className={row.differs ? 'cell-changed' : undefined}>
                  {row.editable ? (
                    <input
                      type="text"
                      className="input"
                      value={value}
                      aria-label={`Proposed ${row.label}`}
                      placeholder={row.currentValue ?? ''}
                      onChange={(event) => onEdit(row.field, event.target.value)}
                    />
                  ) : (
                    row.suggestedValue ?? <span className="muted">(nothing read)</span>
                  )}
                </td>
                <td>
                  {!row.editable ? (
                    <span className="muted" title={row.note ?? undefined}>
                      Read-only
                    </span>
                  ) : !value.trim() || value.trim() === (row.currentValue ?? '') ? (
                    <span className="muted">No change</span>
                  ) : (
                    <CheckboxField
                      label="Overwrite"
                      checked={apply[row.field] === true}
                      onChange={(checked) => onToggle(row.field, checked)}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
    </Panel>
  );
}
